import {
  ApiError as ApiErrorBody,
  ArtifactDownloadResponse,
  CheckoutResponse,
  CreateJobResponse,
  EntitlementListResponse,
  JobListResponse,
  JobReportResponse,
  JobView,
  ProjectInventoryResponse,
  ProjectListResponse,
  ProjectView,
  RecommendResponse,
  TestConnectionResponse,
  type ErrorCode,
  type JobType,
  type PackageId,
} from '@detachly/contracts'
import type { z } from 'zod'

/**
 * Every failure the UI can render arrives as one of these: the server's own error envelope, or a
 * transport failure translated into the same shape. Screens switch on `code`, never on a string.
 */
export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

export type ConnectProjectInput = {
  name: string
  framerUrl: string
  apiKey: string
  ownership: true
}

export type ApiClient = ReturnType<typeof createApiClient>

export type ApiClientOptions = {
  baseUrl: string
  /** Clerk session token. `null` when the user signed out mid-flight. */
  getToken: () => Promise<string | null>
  fetch?: typeof fetch
}

export function createApiClient(options: ApiClientOptions) {
  const send = options.fetch ?? globalThis.fetch.bind(globalThis)
  const base = options.baseUrl.replace(/\/+$/u, '')

  async function request<T>(
    path: string,
    schema: z.ZodType<T>,
    init: RequestInit = {},
  ): Promise<T> {
    const token = await options.getToken()
    if (!token) {
      throw new ApiRequestError('UNAUTHORIZED', 'Your session expired. Sign in again.', 401)
    }

    let response: Response
    try {
      response = await send(`${base}${path}`, {
        ...init,
        headers: {
          ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...init.headers,
          Authorization: `Bearer ${token}`,
        },
      })
    } catch {
      // Offline, DNS, CORS: the user needs a retry button, not a stack trace.
      throw new ApiRequestError('INTERNAL', 'Could not reach Detachly. Check your connection.', 0)
    }

    const payload =
      response.status === 204 ? null : ((await response.json().catch(() => null)) as unknown)

    if (!response.ok) {
      const parsed = ApiErrorBody.safeParse(payload)
      if (parsed.success) {
        throw new ApiRequestError(
          parsed.data.error.code,
          parsed.data.error.message,
          response.status,
          parsed.data.error.details,
        )
      }
      throw new ApiRequestError('INTERNAL', 'Something went wrong. Try again.', response.status)
    }

    const parsed = schema.safeParse(payload)
    if (!parsed.success) {
      // A shape we do not recognise is our bug; showing raw JSON would help nobody.
      console.error('unexpected response shape', { path, issues: parsed.error.issues })
      throw new ApiRequestError(
        'INTERNAL',
        'Detachly returned something unexpected.',
        response.status,
      )
    }
    return parsed.data
  }

  return {
    listProjects: () => request('/projects', ProjectListResponse),

    getProject: (projectId: string) => request(`/projects/${projectId}`, ProjectView),

    connectProject: (input: ConnectProjectInput) =>
      request('/projects', ProjectView, { method: 'POST', body: JSON.stringify(input) }),

    testConnection: (projectId: string, apiKey?: string) =>
      request(`/projects/${projectId}/test-connection`, TestConnectionResponse, {
        method: 'POST',
        body: JSON.stringify(apiKey ? { apiKey } : {}),
      }),

    getInventory: (projectId: string) =>
      request(`/projects/${projectId}/inventory`, ProjectInventoryResponse),

    listJobs: (projectId: string) => request(`/projects/${projectId}/jobs`, JobListResponse),

    startJob: (projectId: string, type: JobType, options?: { retryOfJobId?: string }) =>
      request(`/projects/${projectId}/jobs`, CreateJobResponse, {
        method: 'POST',
        body: JSON.stringify(options?.retryOfJobId ? { type, options } : { type }),
      }),

    getJob: (jobId: string) => request(`/jobs/${jobId}`, JobView),

    getJobReport: (jobId: string) => request(`/jobs/${jobId}/report`, JobReportResponse),

    downloadArtifact: (artifactId: string) =>
      request(`/artifacts/${artifactId}/download`, ArtifactDownloadResponse),

    recommendPackages: (projectId: string) =>
      request(`/billing/packages/recommend?projectId=${projectId}`, RecommendResponse),

    listEntitlements: (projectId: string) =>
      request(`/billing/entitlements?projectId=${projectId}`, EntitlementListResponse),

    startCheckout: (projectId: string, packageId: PackageId) =>
      request('/billing/checkout', CheckoutResponse, {
        method: 'POST',
        body: JSON.stringify({ projectId, packageId }),
      }),
  }
}
